/* RIOF Hero-Karussell — wechselt die Hintergrundbilder der Kopfsektion.
   Ohne JavaScript bleibt das erste Bild stehen, die Sektion sieht aus wie vorher.
   Autoplay pausiert bei prefers-reduced-motion, im Hintergrund-Tab, außerhalb
   des Sichtbereichs und solange die Steuerung den Fokus hat. */
(() => {
  if (customElements.get('riof-hero-karussell')) return;

  const REDUZIERT = '(prefers-reduced-motion: reduce)';

  class RiofHeroKarussell extends HTMLElement {
    connectedCallback() {
      // Farbe des Monats abgelaufen (Seite aus dem Cache): Slide entfernen, bevor gezählt wird
      if (this.dataset.fdmEnde && new Date(this.dataset.fdmEnde + 'T23:59:59+01:00') < new Date()) {
        this.querySelectorAll('[data-riof-hk-fdm]').forEach((s) => s.remove());
        const punkte = this.querySelectorAll('[data-riof-hk-punkt]');
        if (punkte.length) punkte[punkte.length - 1].remove();
        delete this.dataset.fdmLink;
      }
      this.slides = Array.from(this.querySelectorAll('[data-riof-hk-slide]'));
      if (this.slides.length === 0) return;

      this.spur = this.querySelector('[data-riof-hk-spur]');
      this.punkte = Array.from(this.querySelectorAll('[data-riof-hk-punkt]'));
      this.index = 0;
      this.timer = null;
      this.sichtbar = true;

      this.zeige(0);
      if (this.slides.length < 2) return;

      this.intervall = Math.max(2000, parseInt(this.dataset.intervall, 10) || 6000);
      this.dauer = parseInt(getComputedStyle(this).getPropertyValue('--riof-hk-dauer'), 10) || 900;
      this.autoplay = this.dataset.autoplay === 'true' && !window.matchMedia(REDUZIERT).matches;

      this.onKlick = this.onKlick.bind(this);
      this.onSichtbarkeit = this.onSichtbarkeit.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onFokusRein = () => this.stoppe();
      this.onFokusRaus = () => this.starte();

      this.addEventListener('click', this.onKlick);
      this.addEventListener('focusin', this.onFokusRein);
      this.addEventListener('focusout', this.onFokusRaus);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.onSichtbarkeit);

      // Außerhalb des Sichtbereichs nicht weiterblättern — spart Arbeit,
      // und der Besucher verpasst keine Bilder.
      if ('IntersectionObserver' in window) {
        this.beobachter = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              this.sichtbar = entry.isIntersecting;
              if (this.sichtbar) { this.starte(); this.videos(); } else { this.stoppe(); this.querySelectorAll('[data-riof-hk-video] video').forEach((v) => v.pause()); }
            }
          },
          { threshold: 0.15 }
        );
        this.beobachter.observe(this);
      }

      this.starte();
    }

    disconnectedCallback() {
      this.stoppe();
      window.clearTimeout(this.leaveTimer);
      this.removeEventListener('click', this.onKlick);
      this.removeEventListener('focusin', this.onFokusRein);
      this.removeEventListener('focusout', this.onFokusRaus);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('visibilitychange', this.onSichtbarkeit);
      if (this.beobachter) this.beobachter.disconnect();
    }

    zeige(index) {
      const anzahl = this.slides.length;
      const vorher = this.index;
      this.index = ((index % anzahl) + anzahl) % anzahl;

      this.slides.forEach((slide, i) => {
        const aktiv = i === this.index;
        slide.classList.toggle('is-active', aktiv);
        slide.setAttribute('aria-hidden', aktiv ? 'false' : 'true');
      });

      // Das abgelöste Bild behält seinen Ken-Burns-Zoom, bis es ausgeblendet
      // ist — sonst springt es während der Überblendung auf die Ausgangsgröße.
      if (vorher !== this.index) this.entlasse(this.slides[vorher]);

      this.punkte.forEach((punkt, i) => {
        const aktiv = i === this.index;
        punkt.classList.toggle('is-active', aktiv);
        punkt.setAttribute('aria-current', aktiv ? 'true' : 'false');
      });

      if (this.spur) this.spur.style.setProperty('--riof-hk-index', String(this.index));
      this.videos();
    }

    // RIOF Video-Slide: nur das sichtbare Video (Desktop/Handy) des aktiven Slides läuft, ohne Ton.
    sichtbaresVideo(slide) {
      if (!slide || !slide.hasAttribute('data-riof-hk-video')) return null;
      return Array.from(slide.querySelectorAll('video')).find((v) => getComputedStyle(v).display !== 'none') || null;
    }

    videos() {
      const aktiv = this.slides[this.index];
      // RIOF Farbe des Monats: eigener Slide mit eigenem Text, Klick führt zum Farbband
      this.classList.toggle('riof-hk--fdm-aktiv', !!(aktiv && aktiv.hasAttribute('data-riof-hk-fdm')));
      const video = this.sichtbaresVideo(aktiv);
      this.classList.toggle('riof-hk--video-aktiv', !!video);
      this.querySelectorAll('[data-riof-hk-video] video').forEach((v) => {
        if (v !== video) { v.pause(); if (v.currentTime) v.currentTime = 0; }
      });
      if (!video) return;
      video.muted = true; video.playsInline = true; video.loop = false; video.controls = false;
      video.preload = 'auto';
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }

    entlasse(slide) {
      if (!slide) return;
      slide.classList.add('is-leaving');
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = window.setTimeout(() => {
        this.slides.forEach((s) => {
          if (!s.classList.contains('is-active')) s.classList.remove('is-leaving');
        });
      }, this.dauer);
    }

    weiter(schritt) {
      this.zeige(this.index + schritt);
      this.starte();
    }

    starte() {
      this.stoppe();
      if (!this.autoplay || !this.sichtbar || document.hidden) return;
      if (this.contains(document.activeElement)) return;
      const naechster = () => { this.zeige(this.index + 1); this.starte(); };
      const video = this.sichtbaresVideo(this.slides[this.index]);
      if (video) {
        // Video-Slide: weiter, wenn das Video zu Ende ist (Sicherheitsnetz, falls es nicht abspielt)
        if (video.paused) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
        this.videoEnde = () => naechster();
        video.addEventListener('ended', this.videoEnde, { once: true });
        this.aktivesVideo = video;
        const rest = isFinite(video.duration) && video.duration > 0 ? (video.duration - video.currentTime) * 1000 + 1500 : 25000;
        this.timer = window.setTimeout(naechster, rest);
        return;
      }
      this.timer = window.setTimeout(naechster, this.intervall);
    }

    stoppe() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
      if (this.aktivesVideo && this.videoEnde) this.aktivesVideo.removeEventListener('ended', this.videoEnde);
      this.aktivesVideo = null;
    }

    onKlick(event) {
      const punkt = event.target.closest('[data-riof-hk-punkt]');
      if (punkt) {
        this.weiter(this.punkte.indexOf(punkt) - this.index);
        return;
      }

      const pfeil = event.target.closest('[data-riof-hk-pfeil]');
      if (pfeil) { this.weiter(pfeil.dataset.riofHkPfeil === 'zurueck' ? -1 : 1); return; }

      // RIOF: Antippen des Farbe-des-Monats-Slides führt zum Farbband auf der Watzmann-Seite
      if (this.dataset.fdmLink && this.classList.contains('riof-hk--fdm-aktiv') && !event.target.closest('button, [data-riof-hk-punkt], [data-riof-hk-pfeil]')) {
        event.preventDefault();
        window.location.href = this.dataset.fdmLink;
        return;
      }

      // RIOF: Antippen des laufenden Videos führt zum Konfigurator
      if (this.dataset.videoLink && this.classList.contains('riof-hk--video-aktiv') && !event.target.closest('a, button')) {
        window.location.href = this.dataset.videoLink;
      }
    }

    onSichtbarkeit() {
      document.hidden ? this.stoppe() : this.starte();
    }

    onTouchStart(event) {
      this.wischStart = event.changedTouches[0].clientX;
    }

    onTouchEnd(event) {
      if (typeof this.wischStart !== 'number') return;
      const weg = event.changedTouches[0].clientX - this.wischStart;
      this.wischStart = null;
      if (Math.abs(weg) < 40) return;
      this.weiter(weg < 0 ? 1 : -1);
    }
  }

  customElements.define('riof-hero-karussell', RiofHeroKarussell);
})();
